// Helpers for the fitOptions flow: scope the search to size buckets that can fit the shopper's
// space, then keep only products whose exact dimensions fit.

import { FIT_DIMENSION_SPEC_NAMES } from '../elligible-items/product-pod/dimensions';

const DIMENSIONS = ['width', 'height', 'depth'];

// Search size facets used to narrow results. The dimension IDs are the same across refrigerator
// subclasses (French door, top/bottom freezer, side by side).
// - height uses "Height to Top of Refrigerator Case": the case is never taller than the overall
//   product, so a product that fits by overall height always lands in a bucket we keep. The exact
//   "Overall Product Height" facet has too many values to fit in the refinement budget.
export const SIZE_FACETS = {
  width: '4295171865', // Refrigerator Fit Width
  height: '587404', // Height to Top of Refrigerator Case (in.)
  depth: '511817' // Depth (Excluding Handles) (in.)
};

// Search applies at most 8 refinements per navParam and silently drops the rest, which would
// drop products that fit, so the combined refinements are kept within this budget.
export const MAX_REFINEMENTS = 8;

// Nominal width buckets ("36 Inch Wide") hold products up to ~7/8" narrower than the label.
export const NOMINAL_SIZE_TOLERANCE = 1;

// The products(itemIds) query accepts at most 16 item IDs per call
export const SPECS_BATCH_SIZE = 16;

// Same spec precedence fit-compatibility uses, so both agree on what fits; shared with the displayed
// dimensions so the pod shows exactly what was compared
const PRODUCT_DIMENSION_SPECS = FIT_DIMENSION_SPEC_NAMES;

const toNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Normalizes userDimensions ({ width, height, depth } as strings or numbers) to numbers.
 * Returns null when no dimension is usable, which turns size filtering off.
 */
export const parseUserDimensions = (userDimensions) => {
  const parsed = DIMENSIONS.reduce((acc, dimension) => ({
    ...acc,
    [dimension]: toNumber(userDimensions?.[dimension])
  }), {});
  return DIMENSIONS.some((dimension) => parsed[dimension] !== null) ? parsed : null;
};

/**
 * Smallest size a refinement bucket can contain, parsed from its label.
 * Returns null for labels that can't be parsed (e.g. "Others") so they are kept and left to the
 * exact dimension check.
 */
export const getRefinementMinimum = (label = '') => {
  const text = label.trim();
  let match = text.match(/^less than\s+[\d.]+/i);
  if (match) return 0;
  match = text.match(/^greater than\s+([\d.]+)/i);
  if (match) return parseFloat(match[1]);
  match = text.match(/^([\d.]+)\s*inch\s+\w+\s+or greater/i);
  if (match) return parseFloat(match[1]);
  match = text.match(/^([\d.]+)\s*-\s*[\d.]+/);
  if (match) return parseFloat(match[1]);
  match = text.match(/^([\d.]+)\s*inch\s+(wide|tall|deep)$/i);
  if (match) return parseFloat(match[1]) - NOMINAL_SIZE_TOLERANCE;
  match = text.match(/^([\d.]+)\s*in\.?$/i);
  if (match) return parseFloat(match[1]);
  return null;
};

const countRecords = (refinements) => refinements.reduce((sum, { recordCount }) => sum + (Number(recordCount) || 0), 0);

// Every subset of the candidate facets, used to pick the most selective combination in budget
const getSubsets = (items) => items.reduce(
  (subsets, item) => subsets.concat(subsets.map((subset) => [...subset, item])),
  [[]]
);

/**
 * Builds the navParam for the product search, adding the size buckets that can fit maxDimensions.
 * Returns null when a dimension has no bucket that can fit, meaning nothing in the category fits.
 */
export const getSizeRefinedNavParam = ({ navParam, dimensions, maxDimensions }) => {
  const candidates = [];
  let nothingFits = false;

  DIMENSIONS.forEach((dimension) => {
    const max = maxDimensions?.[dimension];
    const facet = (dimensions || []).find((item) => item?.dimensionId === SIZE_FACETS[dimension]);
    if (max == null || !facet?.refinements?.length) return;

    const fitting = facet.refinements.filter((refinement) => {
      const minimum = getRefinementMinimum(refinement.label);
      return minimum === null || minimum <= max;
    });
    if (!fitting.length) {
      nothingFits = true;
      return;
    }
    // When every bucket can fit, the refinement narrows nothing
    if (fitting.length === facet.refinements.length) return;

    const total = countRecords(facet.refinements);
    candidates.push({
      keys: fitting.map(({ refinementKey }) => refinementKey),
      keptShare: total ? countRecords(fitting) / total : 1
    });
  });

  if (nothingFits) return null;

  // Facets combine with AND, so the share of products kept is roughly the product of each share
  const best = getSubsets(candidates)
    .filter((subset) => subset.reduce((sum, { keys }) => sum + keys.length, 0) <= MAX_REFINEMENTS)
    .reduce((winner, subset) => {
      const keptShare = subset.reduce((share, candidate) => share * candidate.keptShare, 1);
      return keptShare < winner.keptShare ? { subset, keptShare } : winner;
    }, { subset: [], keptShare: 1 });

  return navParam + best.subset.flatMap(({ keys }) => keys).map((key) => `Z${key}`).join('');
};

/** Exact product dimensions (numbers or null) from a product's specificationGroup */
export const getProductDimensions = (specificationGroup) => {
  const specifications = (specificationGroup || [])
    .flatMap((group) => group?.specifications || []);
  return DIMENSIONS.reduce((acc, dimension) => {
    const value = PRODUCT_DIMENSION_SPECS[dimension]
      .map((specName) => toNumber(specifications.find((spec) => spec?.specName === specName)?.specValue))
      .find((number) => number !== null);
    return { ...acc, [dimension]: value ?? null };
  }, {});
};

/**
 * True when every constrained dimension is known and within its maximum. A missing measurement
 * counts as not fitting, so a product is never recommended without verifying its size.
 */
export const fitsDimensions = (productDimensions, maxDimensions) => DIMENSIONS.every((dimension) => {
  const max = maxDimensions?.[dimension];
  if (max == null) return true;
  const value = productDimensions?.[dimension];
  return value != null && value <= max;
});

const normalize = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '') || null;

const findSpec = (specificationGroup, specName) => (specificationGroup || [])
  .flatMap((group) => group?.specifications || [])
  .find((spec) => spec?.specName === specName)?.specValue;

/** Finish name and family, lowercased for comparison (e.g. "printproof stainless steel" / "stainless steel") */
export const getFinish = (specificationGroup) => ({
  finish: normalize(findSpec(specificationGroup, 'Color/Finish')),
  family: normalize(findSpec(specificationGroup, 'Color/Finish Family'))
});

// 0 = same finish, 1 = same finish family, 2 = different or unknown. Finish names vary by brand
// ("PrintProof Stainless Steel", "Fingerprint Resistant Stainless Steel"), so family is the fallback.
const getFinishRank = (finish, currentFinish) => {
  if (finish.finish && finish.finish === currentFinish.finish) return 0;
  if (finish.family && finish.family === currentFinish.family) return 1;
  return 2;
};

// Handles Infinity (missing price) without producing NaN the way subtraction would
const compareNumbers = (left, right) => {
  if (left === right) return 0;
  return left < right ? -1 : 1;
};

const getPriceDistance = (price, currentPrice) => {
  const value = parseFloat(price);
  const current = parseFloat(currentPrice);
  return Number.isFinite(value) && Number.isFinite(current) ? Math.abs(value - current) : Infinity;
};

/**
 * Orders products by similarity to the current item: same finish first, then closest price, then same
 * brand. Remaining ties keep the incoming (search) order. Missing data ranks after known data.
 * - products: search results ({ itemId, pricing.value, identifiers.brandName })
 * - specificationGroups: { [itemId]: specificationGroup } for the products
 * - currentItem: { specificationGroup, price, brandName } for the item being replaced
 */
export const sortBySimilarity = (products, { specificationGroups = {}, currentItem } = {}) => {
  if (!currentItem) return products;
  const currentFinish = getFinish(currentItem.specificationGroup);
  const currentBrand = normalize(currentItem.brandName);

  return (products || [])
    .map((product, index) => {
      const brand = normalize(product?.identifiers?.brandName);
      return {
        product,
        index,
        finishRank: getFinishRank(getFinish(specificationGroups[product?.itemId]), currentFinish),
        priceDistance: getPriceDistance(product?.pricing?.value, currentItem.price),
        brandRank: brand && brand === currentBrand ? 0 : 1
      };
    })
    .sort((left, right) => compareNumbers(left.finishRank, right.finishRank)
      || compareNumbers(left.priceDistance, right.priceDistance)
      || compareNumbers(left.brandRank, right.brandRank)
      || compareNumbers(left.index, right.index))
    .map(({ product }) => product);
};
